import React, { useState, useRef } from 'react';
import { Bar } from 'react-chartjs-2';
import { ChartOptions } from 'chart.js';
import { ChartControls, LoadingSpinner, DataTable } from '../ui';
import { useTheme } from '../../contexts/ThemeContext';
import { getChartJsOptions, getChartColors, getChartBackgroundColor, mergeChartOptions } from '../../utils/themeHelpers';
import { formatNumber } from '../../utils/formatters';
import useChartGuide from '../../hooks/useChartGuide';
import { useFavoriteToggle } from '../../hooks/useFavoriteToggle';
import { CHART_GUIDES } from '../../constants/chartGuides';
import { useRefineCustomerDissatisfactionReasons, useRefineCustomerDissatisfactionDetails } from '../../hooks/useRefineCustomerQueries';
import ReactDOM from 'react-dom';
import { X, Info } from 'lucide-react';

interface DissatisfactionReasonsProps {
  branchId?: number;
  showLoading?: boolean;
  onError?: (error: Error) => void;
  limit?: number;
}

// Modal Component for showing details
const DissatisfactionDetailsModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  categoryName: string;
  branchId: number;
}> = ({ isOpen, onClose, categoryName, branchId }) => {
  const { data: details, isLoading } = useRefineCustomerDissatisfactionDetails(branchId, categoryName);

  React.useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  // Prepare data for DataTable
  const tableData = details ? details.map((item) => {
    const total = details.reduce((sum, d) => sum + d.count, 0);
    const percentage = ((item.count / total) * 100).toFixed(1);
    return {
      reason: item.reason,
      count: formatNumber(item.count),
      percentage: `${percentage}%`
    };
  }) : [];

  const columns = [
    { key: 'reason', title: 'دلیل' },
    { key: 'count', title: 'تعداد' },
    { key: 'percentage', title: 'درصد' }
  ];

  return ReactDOM.createPortal(
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
        style={{ zIndex: 1000000 }}
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="fixed inset-0 overflow-y-auto" style={{ zIndex: 1000001 }}>
        <div className="flex min-h-full items-center justify-center p-8 text-center">
          <div className="card-base card-padding animate-enter animate-delay-3 relative transform overflow-hidden text-right shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-2xl">
            {/* Close button */}
            <button
              onClick={onClose}
              className="absolute top-4 left-4 hover-effect p-2 text-text-secondary hover:text-text-primary hover:bg-bg-secondary rounded-lg"
            >
              <X className="h-5 w-5" />
            </button>
            
            <h3 className="title-card mb-4">جزئیات دلیل نارضایتی: {categoryName}</h3>
            
            {isLoading ? (
              <LoadingSpinner variant="minimal" size="lg" message="در حال بارگذاری جزئیات…" />
            ) : !details || details.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-text-secondary">جزئیاتی برای این دسته‌بندی یافت نشد</p>
              </div>
            ) : (
              <DataTable
                data={tableData}
                columns={columns}
                striped={true}
              />
            )}
          </div>
        </div>
      </div>
    </>,
    document.body
  );
};

const DissatisfactionReasons: React.FC<DissatisfactionReasonsProps> = ({
  branchId = 0,
  showLoading = true,
  onError,
  limit = 10
}) => {
  const chartRef = useRef<HTMLDivElement>(null);
  const { theme: currentTheme } = useTheme();
  const { openGuide } = useChartGuide();
  const { handleFavoriteToggle, isFavorited } = useFavoriteToggle();
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  
  // Component metadata
  const componentId = 'dissatisfaction-reasons';
  const componentTitle = 'گزارش بیشترین دلایل نارضایتی در یک ماه اخیر';
  
  // Fetch data using individual hook
  const { data: topDissatisfactionReasons, isLoading, error } = useRefineCustomerDissatisfactionReasons(branchId, limit);

  // Handle error
  React.useEffect(() => {
    if (error && onError) {
      onError(error as Error);
    }
  }, [error, onError]);

  if (isLoading && showLoading) {
    return (
            <LoadingSpinner
              variant="minimal"
              size="lg"
              message="در حال بارگذاری…"
            />
    );
  }

  if (error || !topDissatisfactionReasons) {
    return null;
  }

  const dissatisfactionReasonsData = {
    labels: (topDissatisfactionReasons || []).map((item) => item.reason),
    datasets: [{
      label: 'تعداد',
      data: (topDissatisfactionReasons || []).map((item) => item.count),
      backgroundColor: getChartBackgroundColor(1, currentTheme,1),
      borderColor: getChartColors(currentTheme)[1],
      borderWidth: 1
    }]
  };

  // Handle bar click
  const handleBarClick = (_event: any, elements: any[]) => {
    if (elements.length > 0) {
      const index = elements[0].index;
      const category = topDissatisfactionReasons?.[index]?.reason;
      if (category) {
        setSelectedCategory(category);
        setIsModalOpen(true);
      }
    }
  };

  // Bar chart options using theme helpers
  const barChartOptions: ChartOptions<'bar'> = mergeChartOptions(getChartJsOptions(currentTheme), {
    indexAxis: 'x' as const,
    ...getChartJsOptions(currentTheme),
    interaction: {
      mode: 'index' as const,
      intersect: false,
    },
    onHover: (event: any, activeElements: any[]) => {
      (event.native.target as HTMLElement).style.cursor = activeElements.length > 0 ? 'pointer' : 'default';
    },
    onClick: handleBarClick,
    plugins: {
      ...getChartJsOptions(currentTheme).plugins,
      legend: { display: false },
      tooltip: {
        ...getChartJsOptions(currentTheme).plugins?.tooltip,
        callbacks: {
          label: function (context: any) {
            return `تعداد: ${formatNumber(context.parsed.y)}`;
          },
          afterLabel: function() {
            return 'برای مشاهده جزئیات کلیک کنید';
          }
        }
      }
    },
    scales: {
      x: {
        grid: {
          display: false,
        },
        ticks: {
          autoSkip: false,
          maxRotation: 45,
          minRotation: 45
        }
      },
      y: {
        grid: {
          display: false,
        },
        beginAtZero: true,
        ticks: {
          callback: function (value: any) {
            return formatNumber(value);
          }
        }
      }
    },
  });

  return (
    <div ref={chartRef} className="card-base card-padding animate-enter animate-delay-3">
      <ChartControls
        componentId={componentId}
        componentTitle={componentTitle}
        componentType="chart"
        dashboardSource="refineCustomer"
        onInfo={() => openGuide(CHART_GUIDES['dissatisfaction-reasons'])}
        onFavorite={() => handleFavoriteToggle({
          componentId,
          componentTitle,
          dashboardSource: 'refineCustomer',
          componentType: 'chart',
          description: 'نمودار میله‌ای دلایل نارضایتی مشتریان',
          props: { branchId, showLoading, onError, limit },
          gridLayout: {
            containerClass: 'card-base card-padding'
          }
        })}
        isFavorited={isFavorited(componentId)}
        chartRef={chartRef}
        className="mb-4"
      />
      <h3 className="title-card mb-4">{componentTitle}</h3>
      
      {/* Instruction text */}
      <div className="flex items-center gap-2 mb-4 text-sm text-text-secondary">
        <Info className="h-4 w-4" />
        <span>برای مشاهده جزئیات هر دسته، روی میله‌های نمودار کلیک کنید</span>
      </div>
      
      <div style={{ height: 400, width: '100%' }}>
        <Bar data={dissatisfactionReasonsData} options={barChartOptions} />
      </div>
      
      {/* Details Modal */}
      {selectedCategory && (
        <DissatisfactionDetailsModal
          isOpen={isModalOpen}
          onClose={() => {
            setIsModalOpen(false);
            setSelectedCategory(null);
          }}
          categoryName={selectedCategory}
          branchId={branchId}
        />
      )}
    </div>
  );
};

export default DissatisfactionReasons;